import { useCallback, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { MultiValue, SingleValue } from "react-select";
import Button from "../../../../components/button";
import { AppDispatch } from "../../../../redux/store";
import {
  getReasonsState,
  ReasonResponse,
  reasonsStateAction,
} from "../../../../redux/features/other-slice";
import { OptionValue } from "../../../../types/component";
import { isSelectSingleValue } from "../../../../core/utils";
import { getUserState } from "../../../../redux/features/auth-slice";
import StyledSelect from "../../../../components/styled-select";
import { InfoOutlined } from "../../../../components/icons/info-outlined";
import Input from "../../../../components/input";
import {
  SummaryReason,
  WeeklySummaryResponse,
} from "../../../../redux/features/weekly-summary-slice";
import SummaryPropertyDiscussDialog from "../summary-property-discuss-dialog";
import { EditOutlined } from "../../../../components/icons/edit-outlined";
import { TrashOutlined } from "../../../../components/icons/trash-outlined";
import { CheckOutlined } from "../../../../components/icons/check-outlined";

interface Props {
  summary: WeeklySummaryResponse | null;
  reasons: SummaryReason[];
  setReasons: (values: SummaryReason[]) => void;
  onRefresh: () => void;
}

export default function ReasonForm(props: Props) {
  const { summary, reasons, setReasons, onRefresh } = props;

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const reasonsState = useSelector(getReasonsState);

  const [selectedReason, setSelectedReason] = useState<SummaryReason | null>(
    null
  );
  const [editId, setEditId] = useState<string | null>(null); 
  const [editValues, setEditValues] = useState<SummaryReason | null>(null); 
  const fetchReasons = useCallback(async () => {
    if (user) {
      await dispatch(
        reasonsStateAction({
          categoryId: user.category.id,
        })
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);
  
  const getAvailableOptions = () => {
    const selectedTypes = reasons.map((reason) => reason.type.id);
    return reasonsState.data.filter(
      (reason: ReasonResponse) => !selectedTypes.includes(reason.id)
    );
  };

  return (
    <>
      <div className="flex flex-col space-y-4">
        {reasons.map((rea: SummaryReason) => {
          const isEditing = editId === rea.type.id; 
          return (
            <div
              key={rea.type.id}
              className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
            >
              <div className="w-full flex flex-col space-y-4">
                <div>
                  <label className="text-xs text-text-title">Type</label>
                  {isEditing ? (
                    <StyledSelect
                      loading={reasonsState.loading}
                      menuPosition="fixed"
                      options={getAvailableOptions().map(
                        (reason: ReasonResponse) => ({
                          value: reason.id,
                          label: reason.title,
                        })
                      )}
                      isClearable={false}
                      value={
                        editValues && editValues.type.id && editValues.type.title
                          ? {
                              value: editValues.type.id,
                              label: editValues.type.title,
                            }
                          : undefined 
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setEditValues({
                            ...editValues!,
                            type: {
                              id: value.value as string,
                              title: value.label as string,
                            },
                          });
                        }
                      }}
                      exClassName="w-full"
                    />
                  ) : (
                    <Input type="text" value={rea.type.title} readonly />
                  )}
                </div>
                <div>
                  <label className="text-xs text-text-title">Detail</label>
                  <Input
                    type="text"
                    inputType="textarea"
                    rows={3}
                    value={isEditing ? editValues?.detail : rea.detail}
                    readonly={!isEditing}
                    errorText={
                      isEditing && editValues?.detail === ""
                        ? "This field is required"
                        : undefined
                    }
                    onChange={(event) => {
                      if (isEditing) {
                        setEditValues({
                          ...editValues!,
                          detail: event.target.value,
                        });
                      }
                    }}
                  />
                </div>
              </div>
              <div className="w-full flex items-center justify-between space-x-2">
                {summary &&
                  summary.reasons.findIndex(
                    (e) => e.type.id === rea.type.id && e.detail === rea.detail
                  ) !== -1 && (
                    <Button
                      variant="outlined"
                      label={`Discusses (${rea.discuss.length})`}
                      onClick={() => setSelectedReason(rea)}
                      exClassName="w-fit border-divider"
                    />
                  )}
                <div className="flex flex-1 items-center justify-end space-x-2">
                  <div
                    className="p-2 rounded cursor-pointer hover:bg-gray-200"
                    onClick={() => {
                      if (isEditing) {
                        // Save logic
                        if (
                          editValues?.type.id &&
                          editValues.type.title &&
                          editValues.detail
                        ) {
                          const editIndex = reasons.findIndex(
                            (e) =>
                              e.type.id === rea.type.id && e.detail === rea.detail
                          );
                          if (editIndex !== -1) {
                            const tmp = [...reasons];
                            tmp[editIndex] = {
                              ...tmp[editIndex],
                              type: editValues.type,
                              detail: editValues.detail,
                            };
                            setReasons(tmp);
                            setEditId(null);
                            setEditValues(null);
                          }
                        }
                      } else {
                        // Enter edit mode
                        setEditId(rea.type.id);
                        setEditValues(rea);
                      }
                    }}
                  >
                    {isEditing ? (
                      <CheckOutlined className="h-4 w-4 text-success-main" />
                    ) : (
                      <EditOutlined className="h-4 w-4" />
                    )}
                  </div>
                  <div
                    className="p-2 rounded cursor-pointer hover:bg-gray-200"
                    onClick={() => {
                      const removedIndex = reasons.findIndex(
                        (e) =>
                          e.type.id === rea.type.id && e.detail === rea.detail
                      );
                      if (removedIndex !== -1) {
                        const tmp = reasons.filter(
                          (_, index) => index !== removedIndex
                        );
                        setReasons(tmp);
                      }
                    }}
                  >
                    <TrashOutlined className="h-4 w-4 text-error-main" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <Formik
          initialValues={{
            reasonId: "",
            reasonTitle: "",
            detail: "",
          }}
          validationSchema={Yup.object().shape({
            reasonId: Yup.string().required("This field is required"),
            reasonTitle: Yup.string().required("This field is required"),
            detail: Yup.string().required("This field is required"),
          })}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(false);
            setReasons([
              ...reasons,
              {
                type: { id: values.reasonId, title: values.reasonTitle },
                detail: values.detail,
                discuss: [],
              },
            ]);
            resetForm();
          }}
        >
          {({ values, setValues, errors, handleChange, handleSubmit }) => (
            <Form
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              className="flex flex-col items-center space-y-4 border border-divider rounded-lg p-4"
            >
              <div className="w-full flex flex-col space-y-4">
                <div>
                  <label className="text-xs text-text-title">Type</label>
                  <div className="flex flex-col">
                    <StyledSelect
                      loading={reasonsState.loading}
                      menuPosition="fixed"
                      options={getAvailableOptions().map(
                        (reason: ReasonResponse) => ({
                          value: reason.id,
                          label: reason.title,
                        })
                      )}
                      isClearable={false}
                      value={
                        values.reasonId !== "" && values.reasonTitle !== ""
                          ? {
                              value: values.reasonId,
                              label: values.reasonTitle,
                            }
                          : { value: "", label: "Select..." }
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setValues({
                            ...values,
                            reasonId: value.value as string,
                            reasonTitle: value.label as string,
                          });
                        }
                      }}
                      exClassName="w-full"
                    />
                    {values.reasonId && (
                      <label className="flex items-center text-text-title text-xs mt-1">
                        <InfoOutlined className="h-4 w-4 mr-1" />
                        {reasonsState.data.filter(
                          (e) => e.id === values.reasonId
                        ).length > 0
                          ? reasonsState.data.filter(
                              (e) => e.id === values.reasonId
                            )[0].description
                          : ""}
                      </label>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-text-title">Detail</label>
                  <Input
                    type="text"
                    inputType="textarea"
                    rows={3}
                    id="detail"
                    value={values.detail}
                    onChange={handleChange}
                    errorText={errors.detail}
                  />
                </div>
              </div>
              <Button
                type="submit"
                label="Add"
                exClassName="w-fit self-end"
                disabled={
                  Array.isArray(errors) ||
                  Object.values(errors).toString() != ""
                }
              />
            </Form>
          )}
        </Formik>
      </div>
      {summary && (
        <SummaryPropertyDiscussDialog
          summaryId={summary.id}
          type="reason"
          // setType={setDiscussPropType}
          property={selectedReason}
          setProperty={setSelectedReason}
          onSubmitted={onRefresh}
        />
      )}
    </>
  );
}
